import { useCallback, useMemo } from 'react';

import useProductQueryParams from 'hooks/use-product-query-params';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import { CHIP_LABELS, getFilterLabel, SpecialFilterKey, toggleSpecialFilter } from 'src/hooks/use-special-filter-param';

type SpecialFilterOption = {
  key: string;
  label: string;
  value: string;
};

type UseSpecialsFilterReturn = {
  options: SpecialFilterOption[];
  selectedFilters: string[];
  handleSelection: (value: string) => void;
  heading: string;
};

const FILTER_KEYS = Object.keys(CHIP_LABELS) as SpecialFilterKey[];

function buildOptions(hideSalesLanguage: boolean): SpecialFilterOption[] {
  return FILTER_KEYS.map((key) => ({
    key,
    label: getFilterLabel(key, hideSalesLanguage),
    value: key,
  }));
}

export function useSpecialsFilter(): UseSpecialsFilterReturn {
  const { queryParams, setQueryParams, defaults } = useProductQueryParams();
  const hideSalesLanguage = useHideSalesLanguage();

  const selectedFilters: string[] = useMemo(() => queryParams.specialFilter ?? [], [queryParams.specialFilter]);
  const options = useMemo(() => buildOptions(hideSalesLanguage), [hideSalesLanguage]);
  const heading = hideSalesLanguage ? 'Offers' : 'Sales & Offers';

  const handleSelection = useCallback(
    (value: string) => {
      const next = toggleSpecialFilter(selectedFilters, value);
      setQueryParams({
        specialFilter: next.length > 0 ? next : undefined,
        page: defaults.page,
      });
    },
    [selectedFilters, setQueryParams, defaults.page]
  );

  return {
    options,
    selectedFilters,
    handleSelection,
    heading,
  };
}
